import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const empty = { url: "", platform_id: "", buyer_name: "", vendor_name: "", price: "", status: "pending" };

export default function AddEditLinkModal({ open, onClose, platforms, defaultPlatformId, editing, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        url: editing.url,
        platform_id: editing.platform_id,
        buyer_name: editing.buyer_name || "",
        vendor_name: editing.vendor_name || "",
        price: editing.price?.toString() ?? "",
        status: editing.status || "pending",
      });
    } else {
      setForm({ ...empty, platform_id: defaultPlatformId || (platforms[0]?.id ?? "") });
    }
  }, [open, editing, defaultPlatformId, platforms]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url.trim() || !form.platform_id) {
      toast.error("URL and platform are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        url: form.url.trim(),
        platform_id: form.platform_id,
        buyer_name: form.buyer_name.trim(),
        vendor_name: form.vendor_name.trim(),
        price: parseFloat(form.price) || 0,
        status: form.status,
      };
      if (editing) {
        const { data } = await api.put(`/links/${editing.id}`, payload);
        toast.success("Link updated");
        onSaved(data, true);
      } else {
        const { data } = await api.post("/links", payload);
        toast.success("Link tracked");
        onSaved(data, false);
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0E131F] border border-slate-800 chamfered max-w-lg" data-testid="add-link-modal">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wide text-amber-500">
            {editing ? "Update Link Record" : "New Link Record"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-mono block mb-1">Link URL *</label>
            <input
              required
              type="text"
              placeholder="https://…"
              value={form.url}
              onChange={(e) => update("url", e.target.value)}
              className="input-tactical w-full px-3 py-2 rounded"
              data-testid="input-link-url"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1">Platform *</label>
              <Select value={form.platform_id} onValueChange={(v) => update("platform_id", v)}>
                <SelectTrigger className="bg-[#0B0E17] border-slate-800 text-slate-200" data-testid="select-link-platform">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent className="bg-[#0E131F] border-slate-800 text-slate-200">
                  {platforms.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-mono block mb-1">Status</label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger className="bg-[#0B0E17] border-slate-800 text-slate-200" data-testid="select-link-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0E131F] border-slate-800 text-slate-200">
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="ongoing">Ongoing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="submitted">Submitted for Approval</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1">Buyer Name</label>
              <input
                value={form.buyer_name}
                onChange={(e) => update("buyer_name", e.target.value)}
                className="input-tactical w-full px-3 py-2 rounded"
                data-testid="input-buyer-name"
              />
            </div>
            <div>
              <label className="label-mono block mb-1">Vendor Name</label>
              <input
                value={form.vendor_name}
                onChange={(e) => update("vendor_name", e.target.value)}
                className="input-tactical w-full px-3 py-2 rounded"
                data-testid="input-vendor-name"
              />
            </div>
          </div>
          <div>
            <label className="label-mono block mb-1">Price / Amount ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.price}
              onChange={(e) => update("price", e.target.value)}
              placeholder="0.00"
              className="input-tactical w-full px-3 py-2 rounded"
              data-testid="input-link-price"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="btn-signal w-full py-2.5 rounded"
            data-testid="save-link-submit-btn"
          >
            {busy ? "Saving…" : (editing ? "Update Link" : "Add Link")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
